import { create } from '@bufbuild/protobuf'
import { Timeline } from 'antd'
import { StarsIcon } from 'lucide-react'

import { DocumentQuoteSchema } from '@/gen/artifacts/v1/model_pb'
import { ControlStatus } from '@/gen/controls/v1/control_status_pb'
import { ControlTimelineItem, GetControlResponse } from '@/gen/inventory/v1/control_service_pb'
import { GetSignalResponse } from '@/gen/inventory/v1/signal_service_pb'

import { cn } from '@/lib/ui/style-helpers'

import { ControlTimeline } from '@/pages/control/control-timeline/control-timeline'

import { AttestationEvidence } from '@/components/shared/attestation-evidence/attestation-evidence'
import { ControlGapSignalDetails } from '@/components/shared/control-evidence/common/control-gap-signal-details'
import { CONTROL_EVIDENCE_LABELS } from '@/components/shared/control-evidence/common/labels'
import { NoEvidence } from '@/components/shared/control-evidence/common/no-evidence'
import { ControlStatusIcon } from '@/components/shared/icons/control-status'
import { InsightBadge } from '@/components/shared/insight-badge/insight-badge'
import { Markdown } from '@/components/shared/markdown/markdown'
import { SourceDocumentStaticViewer } from '@/components/shared/source-document-static-viewer/source-document-static-viewer'

type ControlEvidenceProps = {
  control: GetControlResponse
  signals: GetSignalResponse[]
  timeline: ControlTimelineItem[]
}

export const ControlEvidence = ({ signals, control, timeline }: ControlEvidenceProps) => {
  const { quotes, status, details, summary } = control.analysisResult || {}
  const hasLemAiInsights = status || summary || details
  const lastControlChange = timeline.find(({ timelineItem }) => timelineItem.case === 'change')
  const vendorNotesSummary =
    lastControlChange?.timelineItem.case === 'change'
      ? lastControlChange.timelineItem.value.vendorNotesSummary
      : undefined
  const hasEvidence = hasLemAiInsights || vendorNotesSummary

  return (
    <div>
      {control.control?.status === ControlStatus.GAP && signals.length > 0 && (
        <ControlGapSignalDetails signals={signals} />
      )}
      {(hasEvidence || lastControlChange) && (
        <Timeline
          items={[
            ...(lastControlChange && lastControlChange.timelineItem.case === 'change'
              ? [
                  {
                    dot: (
                      <ControlStatusIcon status={lastControlChange.timelineItem.value.newStatus} />
                    ),
                    children: (
                      <ControlTimeline
                        readOnly
                        timelineItems={[lastControlChange]}
                        controlStatusId={control.control?.id || ''}
                      />
                    ),
                  },
                ]
              : []),

            ...(hasEvidence
              ? [
                  {
                    dot: <StarsIcon className='size-4' />,
                    children: (
                      <div>
                        {hasLemAiInsights && (
                          <>
                            <h3
                              className={cn(
                                'mb-2 text-main-foreground-tertiary text-heading-xxsmall',
                              )}
                            >
                              {CONTROL_EVIDENCE_LABELS.lemaAiInsights}
                            </h3>
                            {status && (
                              <InsightBadge
                                status={status}
                                variant={!lastControlChange ? 'inactive' : 'default'}
                                showIcon={false}
                              />
                            )}
                            <div className='mt-2'>{summary}</div>
                            <h4 className='mt-2 text-main-foreground-tertiary text-label-default'>
                              {CONTROL_EVIDENCE_LABELS.details}
                            </h4>
                            <Markdown>{details}</Markdown>
                          </>
                        )}

                        <div className='mt-4 flex flex-col gap-2'>
                          {vendorNotesSummary && <AttestationEvidence notes={vendorNotesSummary} />}
                          {quotes &&
                            quotes.length > 0 &&
                            control.analysisResult?.quotes.map((quote, index) => (
                              <div className='break-inside-avoid-page' key={index}>
                                {quote.quote.case === 'documentQuote' ? (
                                  <SourceDocumentStaticViewer
                                    key={index}
                                    documentQuote={create(DocumentQuoteSchema, {
                                      name: quote.quote.value.name,
                                      quoteEntries: quote.quote.value.quoteEntries,
                                      page: quote.quote.value.page,
                                      expirationDate: quote.quote.value.expirationDate,
                                      source: quote.quote.value.source,
                                      uploadedAt: quote.quote.value.uploadedAt,
                                      subcategory: quote.quote.value.subcategory,
                                    })}
                                  />
                                ) : (
                                  <Markdown>{quote.quote.value}</Markdown>
                                )}
                              </div>
                            ))}
                        </div>
                      </div>
                    ),
                  },
                ]
              : []),
          ]}
        />
      )}

      {/* Attested excluded: we did find something, so the claim contradicts the status. */}
      {!hasEvidence &&
        quotes &&
        quotes.length === 0 &&
        control.control?.status !== ControlStatus.ATTESTED && <NoEvidence />}
    </div>
  )
}
